import { useMemo, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Envelope, Lock, ArrowLeft, CircleNotch } from '@phosphor-icons/react';
import LanguageDropdown from '@/components/LanguageDropdown';
import { getBookingCheckoutResumeState } from '@/utils/bookingCheckout';
import { logApiError, parseApiError } from '../utils/apiErrors';
import PasswordInput from '../components/PasswordInput';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang = 'fr' } = useParams();
  const { t } = useLanguage();
  const resumeState = useMemo(
    () => getBookingCheckoutResumeState(lang, location.state),
    [lang, location.state]
  );
  const isBookingCheckoutResume = resumeState?.from?.hash === '#reserver';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUnverifiedEmail('');
    setLoading(true);

    try {
      const user = await login(email, password);
      // Redirect based on role
      const dashboards = {
        client: `/${lang}/client`,
        driver: `/${lang}/driver`,
        admin: `/${lang}/admin`,
      };
      const from = resumeState?.from;
      if (from?.pathname) {
        navigate(`${from.pathname}${from.search || ''}${from.hash || ''}`, { replace: true });
      } else {
        navigate(dashboards[user.role] || `/${lang}`, { replace: true });
      }
    } catch (err) {
      logApiError('login', err);
      const status = err.response?.status;
      if (status === 403) {
        setUnverifiedEmail(email);
        setError(parseApiError(err, 'Adresse email non vérifiée.'));
      } else {
        setError(parseApiError(err, t('loginError') || 'Erreur de connexion'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4 py-20 sm:px-6" data-testid="login-page">
      <div className="absolute top-4 right-4">
        <LanguageDropdown />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="glass rounded-2xl p-5 sm:p-8">
          {/* Back link */}
          <Link to={`/${lang}`} className="mb-8 inline-flex items-center text-[#A1A1AA] transition-colors hover:text-[#D4AF37]">
            <ArrowLeft size={20} className="mr-2" />
            {t('loginBackHome') || 'Retour à l\'accueil'}
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold font-['Cormorant_Garamond'] gold-text mb-2">
              {t('connexion')}
            </h1>
            <p className="text-[#A1A1AA]">{t('loginSubtitle') || 'Accédez à votre espace personnel'}</p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6" data-testid="login-error">
              {error}
              {unverifiedEmail && (
                <div className="mt-2">
                  <Link
                    to={`/${lang}/verify-email`}
                    state={{ email: unverifiedEmail }}
                    className="text-[#D4AF37] hover:text-[#F0C74A] text-sm underline"
                    data-testid="login-resend-link"
                  >
                    Renvoyer le lien d'activation
                  </Link>
                </div>
              )}
            </div>
          )}

          {isBookingCheckoutResume && (
            <div className="mb-6 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-4 py-3 text-sm text-[#F3D67A]">
              Connectez-vous pour retrouver votre réservation et reprendre automatiquement le paiement sécurisé.
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
              <div className="relative">
                <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  required
                  className="pl-10 bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1A1AA]">{t('loginPassword') || 'Mot de passe'}</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#D4AF37]" />
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 bg-[#1E1E1E] border-white/10 focus:border-[#D4AF37]/50"
                  data-testid="login-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] hover:bg-[#F0C74A] text-[#0A0A0A] font-semibold py-6"
              data-testid="login-submit"
            >
              {loading ? <CircleNotch size={20} className="animate-spin" /> : t('loginSubmit') || 'Se connecter'}
            </Button>
          </form>

          {/* Forgot password link */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => navigate(`/${lang}/forgot-password`)}
              className="text-[#D4AF37] hover:text-[#F0C74A] text-sm transition-colors"
            >
              {t('loginForgotPassword') || 'Mot de passe oublié ?'}
            </button>
          </div>

          {/* Register link */}
          <p className="text-center mt-6 text-[#A1A1AA]">
            {t('loginNoAccount') || 'Pas encore de compte ?'}{' '}
            <Link to={`/${lang}/register`} state={resumeState} className="text-[#D4AF37] hover:underline">
              {t('sinscrire')}
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
